import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { stopAnalytics } from "@/components/CookieConsent";
import { PublicNav } from "@/components/public/PublicNav";
import { PublicFooter } from "@/components/public/PublicFooter";

const SECTIONS = [
  {
    h: "1. Who we are",
    p: "KidPath (KidPath.ca) is a Canadian service that helps families of children on the autism spectrum understand funding, tax credits, school supports, documents and next steps. KidPath provides information and navigation only — not medical, legal, or tax advice — and does not determine eligibility or guarantee funding.",
  },
  {
    h: "2. Information we collect",
    p: "Account details you provide (name, email); your family and child profile answers; intake responses used to build your roadmap; documents you choose to upload; tasks and saved programs; and messages you send to the AI Family Navigator. We also collect basic technical data needed to operate the service (for example, authentication sessions).",
  },
  {
    h: "3. How we use your information",
    p: "To create your personalized roadmap, store your documents securely, power the AI Family Navigator, and operate and improve KidPath. We do not sell your personal information.",
  },
  {
    h: "4. Cookies & analytics (your choice)",
    p: "We use strictly necessary cookies to keep you signed in. With your consent, we use optional analytics (PostHog) to understand how families use KidPath so we can improve it. In line with Canada's PIPEDA, analytics are off by default and only start after you select \u201CAccept all\u201D. Analytics are configured with autocapture and session recording disabled. You can change your choice at any time below.",
  },
  {
    h: "5. The AI Family Navigator",
    p: "Your questions and relevant profile context are sent to our AI provider to generate helpful, plain-language responses. The AI is guardrailed to avoid medical, legal, tax, and eligibility advice. Please avoid sharing information you would not want processed by an AI service.",
  },
  {
    h: "6. Your documents",
    p: "Documents you upload are stored privately and are accessible only to you through short-lived, signed links. You can delete any document at any time from your Document Vault.",
  },
  {
    h: "7. Your rights under PIPEDA",
    p: "You may access, correct, or delete your personal information, and withdraw consent for optional processing at any time. To make a request, contact us using the details below.",
  },
  {
    h: "8. Data retention & security",
    p: "We keep your information for as long as your account is active or as needed to provide the service, and we apply reasonable safeguards to protect it. When you delete content, it is removed from your account.",
  },
  {
    h: "9. Contact",
    p: "Questions or privacy requests: privacy@kidpath.ca. KidPath is Canadian and launching in Ontario first.",
  },
];

export default function Privacy() {
  useEffect(() => { document.title = "Privacy Policy — KidPath"; }, []);

  const managePreferences = () => {
    localStorage.removeItem("kidpath_cookie_consent");
    stopAnalytics();
    toast.success("Reloading so you can update your cookie choices.");
    setTimeout(() => window.location.reload(), 800);
  };

  return (
    <div className="min-h-screen bg-kid-mist flex flex-col" data-testid="privacy-page">
      <PublicNav />

      <main className="max-w-3xl w-full mx-auto px-6 py-10 flex-1">
        <Link to="/" className="inline-flex items-center gap-1.5 text-kid-muted hover:text-kid-teal font-poppins text-sm mb-6">
          <ArrowLeft size={16} /> Back to home
        </Link>

        <div className="kid-card p-8 sm:p-10">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-11 h-11 rounded-2xl bg-kid-teal/10 text-kid-teal grid place-items-center"><ShieldCheck size={22} /></div>
            <h1 className="text-3xl sm:text-4xl font-extrabold text-kid-navy">Privacy Policy</h1>
          </div>
          <p className="text-kid-muted mb-8">Last updated June 2026 · Written in plain language, in line with Canada's PIPEDA.</p>

          <div className="space-y-7">
            {SECTIONS.map((s) => (
              <section key={s.h}>
                <h2 className="text-lg font-bold text-kid-navy mb-1.5">{s.h}</h2>
                <p className="text-kid-ink/80 leading-relaxed">{s.p}</p>
              </section>
            ))}
          </div>

          <div className="mt-9 pt-7 border-t border-kid-line">
            <h2 className="text-lg font-bold text-kid-navy mb-2">Manage cookie preferences</h2>
            <p className="text-kid-muted mb-4">You can review or change your cookie and analytics choices at any time.</p>
            <button onClick={managePreferences} className="kid-btn kid-btn-primary h-11 px-6" data-testid="manage-cookies-button">
              Update cookie preferences
            </button>
          </div>
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
